import { z } from "zod";

/**
 * THE DATA CONTRACT
 * =================
 * This file is the single source of truth for the *shape* of the data.
 * `factors.json` must conform to these schemas. When you (or the AI helping
 * you) edit `factors.json`, the app validates it on load and prints a clear,
 * human-readable error if something is wrong — so you never silently ship a
 * broken factor.
 *
 * To add a NEW field to every factor: add it here once, and it's available
 * (and type-checked) everywhere in the app.
 */

/** How confident we are in a factor's estimate. Drives a colored badge. */
export const ConfidenceSchema = z.enum(["high", "medium", "low"]);

/**
 * How the slider maps positions to values between the three anchors.
 * - "log":    smooth across orders of magnitude (use for probabilities like
 *             1e-6 .. 0.5). This is the usual choice.
 * - "linear": evenly spaced (use for plain quantities, e.g. 2 .. 10).
 */
export const ScaleSchema = z.enum(["log", "linear"]);

/** How a factor's value is rendered to the reader. */
export const FormatSchema = z.enum([
  "probability", // 0..1 shown smartly (e.g. "12%" or "1 in 8,000")
  "percent", // 0..1 shown as "12%"
  "number", // plain number, e.g. "3.4"
  "scientific", // e.g. "4.2 × 10⁻⁶"
]);

/** The three scenario anchors. The slider interpolates between them. */
export const ValuesSchema = z.object({
  pessimistic: z.number(),
  consensus: z.number(),
  optimistic: z.number(),
});

/** A single factor — one link in the chain that collapses the number. */
export const FactorSchema = z.object({
  /** Stable unique key. Never reuse or change once published (used for state). */
  id: z.string().min(1),
  /** Short headline shown on the card. */
  title: z.string().min(1),
  /** Category id — must match one of the ids in `categories`. */
  category: z.string().min(1),
  /** One-line explanation shown on hover / tap. */
  tooltip: z.string().min(1),
  /** Deeper explanation revealed when the card is expanded (plain text). */
  explanation: z.string().default(""),
  /** Optional unit label appended to readouts (e.g. "planets"). */
  unit: z.string().optional(),
  confidence: ConfidenceSchema,
  scale: ScaleSchema.default("log"),
  format: FormatSchema.default("probability"),
  values: ValuesSchema,
});

/** A category groups related factors into a labeled section. */
export const CategorySchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** Short blurb shown under the category heading. */
  blurb: z.string().default(""),
});

/** App-level presentation copy, kept in data so it needs no code changes. */
export const MetaSchema = z.object({
  title: z.string().min(1),
  subtitle: z.string().default(""),
  /** Sentence shown above the final number, e.g. "The odds collapse to". */
  resultLabel: z.string().default("The number collapses to"),
  /** Short framing paragraph for the intro. */
  intro: z.string().default(""),
  /** Optional unit for the FINAL product (e.g. "civilizations"). */
  resultUnit: z.string().optional(),
  /** How to format the final product. Defaults to scientific for big/small. */
  resultFormat: FormatSchema.default("scientific"),
  /**
   * Optional starting quantity the chain multiplies. Think "300 billion stars".
   * If omitted, the funnel starts at 1 and shows a pure probability.
   * When `foundation` is present, the start is computed from it instead and
   * this value is used only as a fallback.
   */
  startingValue: z.number().optional(),
  startingLabel: z.string().optional(),
  /**
   * The three foundational inputs the user can tweak. Their product is the
   * starting number of planets the whole chain collapses from. Editable so the
   * reader can see how sensitive the headline is to the cosmic denominator.
   */
  foundation: z
    .object({
      galaxies: z.number(),
      starsPerGalaxy: z.number(),
      planetsPerStar: z.number(),
    })
    .optional(),
});

/** The whole dataset. This is what `factors.json` must contain. */
export const DataSchema = z.object({
  meta: MetaSchema,
  categories: z.array(CategorySchema).min(1),
  factors: z.array(FactorSchema).min(1),
});

// Inferred TypeScript types — import these anywhere in the app.
export type Confidence = z.infer<typeof ConfidenceSchema>;
export type Scale = z.infer<typeof ScaleSchema>;
export type Format = z.infer<typeof FormatSchema>;
export type Values = z.infer<typeof ValuesSchema>;
export type Factor = z.infer<typeof FactorSchema>;
export type Category = z.infer<typeof CategorySchema>;
export type Meta = z.infer<typeof MetaSchema>;
export type Data = z.infer<typeof DataSchema>;

/** The three named scenarios a user can snap all sliders to. */
export type Scenario = "pessimistic" | "consensus" | "optimistic";
